import * as React from "react";
import { Box, Grid, Link, Typography } from "@mui/material";
import { AppBody } from "../AppBody";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { appHeaderTitle } from "../../common/services/Utilities";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import BackButton from "../../containers/BackButton";
import SelectFilterField from "../SelectFilterField";
import { Query } from "../../interfaces";
import PeerGroups from "../../containers/PeerGroups";
import { standardComparisonOptions } from "./PeerGroupUtilities";
import PeerGroupStepTwo from "../../containers/PeerGroupStepTwo";
import EditColumn from "../../containers/EditColumn";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

interface Props {
  appState: Query;
  updateComparisonType: (comparisonType: string) => void;
}

export default function StandardComparison({
  appState,
  updateComparisonType,
}: Props) {
  useInitialPageLoad("peerGroup");

  const comparisonType = appState.ui.comparisonType;

  const introParagraph = (
    <>
      {appHeaderTitle("standardPeerGroup")}
      <Typography variant="h2">
        Predefined Standard Comparison Reports for FDIC-Insured Institutions
      </Typography>
      <Typography variant="body2" color="primary.main">
        Select standardized peer groupings and create comparison reports.
      </Typography>
      <Typography
        variant="body2"
        color="primary.main"
        id="peer-group-feedback-link"
      >
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=PeerGroupStandard"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  let filteredStandardComparisonOptions = standardComparisonOptions.filter(
    (obj: any) => obj.value === "" || obj.groups?.length !== 1,
  );

  const peerGroups = filteredStandardComparisonOptions
    .map(
      (comparison: any) =>
        comparisonType === comparison.value && comparison.groups,
    )
    .filter((value: any) => value)[0];

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="Standard Comparisons" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        introText={introParagraph}
        backButton={
          <BackButton
            app="FinancialReporting"
            buttonLabel="Back to Financial Reporting"
            variant="dark"
          />
        }
      >
        <Box
          sx={{
            "& > h2": {
              marginTop: "2rem",
            },
            "& .MuiFormControl-fullWidth": {
              maxWidth: 400,
            },
          }}
        >
          <Typography variant="h2" paddingBottom="0.5rem">
            Step 1
          </Typography>
          <Typography
            variant="body2"
            color="primary.main"
            sx={{
              fontWeight: 800,
            }}
          >
            Choose “who” to compare. This defines the columns of your report.
          </Typography>
          <Box display="flex">
            <Box marginRight={1}>
              <SelectFilterField
                disabled={appState.ui.pgcStep === "step3"}
                currentValue={comparisonType}
                fieldName={"standardComparison"}
                options={filteredStandardComparisonOptions}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateComparisonType(e.target.value)
                }
              />
            </Box>
            {appState.ui.pgcStep === "step3" && <EditColumn />}
          </Box>
        </Box>

        {appState.ui.pgcStep === "step1" ? (
          <PeerGroups groups={peerGroups} />
        ) : (
          <PeerGroupStepTwo />
        )}
      </AppBody>
    </>
  );
}
